import {
	Cookies,
	decodeJson,
	ensureErrorType,
	HttpClient,
	HttpClientError,
	HttpRequest,
	HttpRequestOptions,
	HttpRequestResult,
	nextCuid
} from "@2d-front-monorepo/core";
import {InstallationId} from "../../nominalTypes/InstallationId";
import IPermitsAuthentication from "../IPermitsAuthentication";
import IPermitsCustomActions from "../IPermitsCustomActions";
import IPermitsDrivers from "../IPermitsDrivers";
import IPermitsFiles from "../IPermitsFiles";
import IPermitsGlobal from "../IPermitsGlobal.js";
import IPermitsHolder from "../IPermitsHolder";
import IPermitsHttpClient from "../IPermitsHttpClient";
import IPermitsPayments from "../IPermitsPayments";
import IPermitsPermits from "../IPermitsPermits";
import IPermitsResidencies from "../IPermitsResidencies";
import IPermitsTariffs from "../IPermitsTariffs";
import IPermitsVehicles from "../IPermitsVehicles";
import {PermitsAuthentication} from "./PermitsAuthentication";
import {PermitsCustomActions} from "./PermitsCustomActions";
import {PermitsDrivers} from "./PermitsDrivers";
import {PermitsFiles} from "./PermitsFiles";
import {PermitsGlobal} from "./PermitsGlobal";
import {PermitsHolder} from "./PermitsHolder";
import {PermitsPayments} from "./PermitsPayments";
import {PermitsPermits} from "./PermitsPermits";
import {PermitsResidencies} from "./PermitsResidencies";
import {PermitsTariffs} from "./PermitsTariffs";
import {PermitsVehicles} from "./PermitsVehicles";

function cyrb53(value: string, seed: number): number {
	let h1 = 3735928559 ^ seed;
	let h2 = 1103547991 ^ seed;
	for (let i = 0; i < value.length; ++i) {
		let ch = value.charCodeAt(i);
		h1 = Math.imul(h1 ^ ch, 2654435761);
		h2 = Math.imul(h2 ^ ch, 1597334677);
	}
	h1 = Math.imul(h1 ^ (h1 >>> 16), 2246822507);
	h1 ^= Math.imul(h2 ^ (h2 >>> 13), 3266489909);
	h2 = Math.imul(h2 ^ (h2 >>> 16), 2246822507);
	h2 ^= Math.imul(h1 ^ (h1 >>> 13), 3266489909);
	return 4294967296 * (2097151 & h2) + (h1 >>> 0);
}

class PermitsFrontClient extends HttpClient implements IPermitsHttpClient {
	private readonly _globalErrorHandler: (error: Error) => void;
	private readonly _installationId: InstallationId | undefined;
	private readonly _seed: number;
	private readonly _sessionId: string;
	_authentication: IPermitsAuthentication | null;
	_customActions: IPermitsCustomActions | null;
	_drivers: IPermitsDrivers | null;
	_files: IPermitsFiles | null;
	_global: IPermitsGlobal | null;
	_holder: IPermitsHolder | null;
	_payments: IPermitsPayments | null;
	_permits: IPermitsPermits | null;
	_residencies: IPermitsResidencies | null;
	_tariffs: IPermitsTariffs | null;
	_vehicles: IPermitsVehicles | null;

	constructor(baseUrl: string, installationId: InstallationId | undefined, globalErrorHandler: (error: Error) => void) {
		super(baseUrl, {
			credentials: "include"
		});

		this._globalErrorHandler = globalErrorHandler;
		this._global = null;
		this._authentication = null;
		this._files = null;
		this._holder = null;
		this._permits = null;
		this._drivers = null;
		this._residencies = null;
		this._vehicles = null;
		this._payments = null;
		this._tariffs = null;
		this._customActions = null;
		this._sessionId = nextCuid();
		this._installationId = installationId;
		this._seed = Date.now();
		this.addPipeline(this._addHeaders.bind(this));
		this.addPipeline(this._errorHandler.bind(this));
	}

	get global(): IPermitsGlobal {
		return this._global ??= new PermitsGlobal(this);
	}

	get authentication(): IPermitsAuthentication {
		return this._authentication ??= new PermitsAuthentication(this);
	}

	get files(): IPermitsFiles {
		return this._files ??= new PermitsFiles(this);
	}

	get holder(): IPermitsHolder {
		return this._holder ??= new PermitsHolder(this);
	}

	get permits(): IPermitsPermits {
		return this._permits ??= new PermitsPermits(this);
	}

	get drivers(): IPermitsDrivers {
		return this._drivers ??= new PermitsDrivers(this);
	}

	get residencies(): IPermitsResidencies {
		return this._residencies ??= new PermitsResidencies(this);
	}

	get vehicles(): IPermitsVehicles {
		return this._vehicles ??= new PermitsVehicles(this);
	}

	get payments(): IPermitsPayments {
		return this._payments ??= new PermitsPayments(this);
	}

	get tariffs(): IPermitsTariffs {
		return this._tariffs ??= new PermitsTariffs(this);
	}

	get customActions(): IPermitsCustomActions {
		return this._customActions ??= new PermitsCustomActions(this);
	}

	async jsonFetch<T>(url: string, requestMethod: string, body: any | null, requestOptions: HttpRequestOptions | null): Promise<T> {
		let result = await this.fetch(url, requestMethod, body, requestOptions);
		if (result.error != null) {
			throw result.error;
		}
		let text = null;
		let response = result.response;
		try {
			text = await response.text();
		} catch (__e) {
			const e: Error = ensureErrorType(__e);
			throw HttpClient.errorFromError(e, result);
		}

		if (this.isJson(response)) {
			return decodeJson<T>(text)!;
		}

		throw new HttpClientError({
			detail: text,
			status: response.status,
			title: "Not a json response"
		}, result.request, response);
	}

	async nullFetch(url: string, requestMethod: string, body: any | null, requestOptions: HttpRequestOptions | null): Promise<void> {
		let result = await this.fetch(url, requestMethod, body, requestOptions);
		if (result.error != null) {
			throw result.error;
		}
	}

	async textFetch(url: string, requestMethod: string, body: any | null, requestOptions: HttpRequestOptions | null): Promise<string> {
		let result = await this.fetch(url, requestMethod, body, requestOptions);
		if (result.error != null) {
			throw result.error;
		}
		let response = result.response;
		try {
			return await response.text();
		} catch (__e) {
			const e: Error = ensureErrorType(__e);
			throw HttpClient.errorFromError(e, result);
		}
	}

	private _addHeaders(request: HttpRequest, next: (() => Promise<HttpRequestResult>)): Promise<HttpRequestResult> {
		request.headers["X-Csrf-Token"] = Cookies.get("XSRF-TOKEN");
		request.headers["X-Session-Id"] = this._sessionId;
		if (this._installationId != null) request.headers["X-User-Installation"] = this._installationId;
		if (request.body != null && typeof (request.body) === "string" && request.body.length > 0) {
			request.headers["Idempotency-Key"] = cyrb53(request.body, this._seed).toString(16);
		}
		return next();
	}

	private async _errorHandler(request: HttpRequest, next: (() => Promise<HttpRequestResult>)): Promise<HttpRequestResult> {
		const r = await next();
		if (r.error != null) {
			this._globalErrorHandler(r.error);
		}
		return r;
	}
}

export {PermitsFrontClient};
